import { useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useParams } from 'react-router-dom'
import { isEmpty } from 'lodash-es'
import { observer } from 'mobx-react'

import { GLOBAL_ROUTE_PATHS } from 'Router/Router.constants'

import { EntityListCard } from 'Components/EntityListCard/EntityListCard'
import type { EntityProps } from 'Components/EntityListCard/EntityListCard.types'

import { useDefaultListTable } from 'Hooks'

import type {
  getEmployeeRentals,
  getEmployeeRentals_employee_rentals_rentals,
  getEmployeeRentalsVariables,
} from 'Constants/graphql/queries/listing/__generated__/getEmployeeRentals'
import { GET_EMPLOYEE_RENTALS } from 'Constants/graphql/queries/listing/GetEmployeeRentals'

import type { DeviceTypeEnum } from 'Types/deviceTypes'

import { composePath } from 'Portal/src/utilities/composePath/composePath.util'

import type { RentalEntityCardProps } from './RentalEntityCard.types'

export const RentalEntityCard = observer((props: RentalEntityCardProps) => {
  const { testId } = props
  const intl = useIntl()
  const { employeeId, organisationId } = useParams<{
    employeeId?: string
    organisationId?: string
  }>()

  const lazyLoadingProps = useDefaultListTable<
    getEmployeeRentals,
    getEmployeeRentalsVariables
  >({
    customItemsPerPage: 20,
    dataObjectPropertyName: 'rentals',
    extraVariables: {
      employeeId,
    },
    isQueryEmployeeBased: true,
    organisationQuery: GET_EMPLOYEE_RENTALS,
  })

  const generateEntities = useMemo(
    (): EntityProps[] =>
      (
        lazyLoadingProps.data as getEmployeeRentals_employee_rentals_rentals[]
      ).map(({ id, stocklistItem, deviceDefinition }) => {
        const imei = stocklistItem?.imei
          ? stocklistItem.imei
          : stocklistItem?.imei2

        const content = imei ? (
          <p key={id} className="m-0 fs-6">
            IMEI: <span>{imei}</span>
          </p>
        ) : null

        const imagePlaceholderType = deviceDefinition?.productType
          ? (deviceDefinition.productType as DeviceTypeEnum)
          : undefined

        const link = composePath({
          isOrganisation: true,
          params: {
            employeeId,
            organisationId,
            rentalId: id,
          },
          paths: {
            employee: GLOBAL_ROUTE_PATHS.employee.rental,
            organisation: GLOBAL_ROUTE_PATHS.organisation.rental,
          },
        })

        return {
          content,
          id,
          imagePlaceholderType,
          imageSource: deviceDefinition?.imageURL,
          link,
          title:
            deviceDefinition?.name ||
            intl.formatMessage({ id: 'RentalEntityCard_rental_id' }, { id }),
        }
      }),
    [lazyLoadingProps.data, employeeId, intl, organisationId],
  )

  const titleSuffix = useMemo(() => {
    return (
      <span className="ms-2">
        <FormattedMessage id="Total" />:{' '}
        <span>{lazyLoadingProps.totalCount}</span>
      </span>
    )
  }, [lazyLoadingProps.totalCount])

  if (isEmpty(lazyLoadingProps.data) || lazyLoadingProps.error) {
    return null
  }

  return (
    <EntityListCard
      bodyClassName="EntityCardList-rentals overflow-auto"
      entities={generateEntities}
      isLazyLoadingCard
      lazyLoadingProps={lazyLoadingProps}
      testId={testId}
      title="Rentals"
      titleSuffix={titleSuffix}
    />
  )
})
